import { inject, Injectable } from '@angular/core';
import { IdentifierType, MemberIdentifier, MemberInterface } from '@app/core/_models/member-interface';
import {
  createRecaptchaHeaders,
  isRecaptchaTooLowScoreHttpResponse,
  RecaptchaData,
  TooLowRecaptchaScoreError,
} from '@app/core/recaptcha/recaptcha-commons';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import {
  CreateMemberRequestData,
  CreateMemberResponse,
  PublicInfoResponse,
  UnsubscribePayload,
} from '@app/core/members/member.service';
import { environment } from '@env/environment';
import { BaseParamsService } from '@app/core/base-params.service';
import { ClientSecret } from '@app/core/_models/client-secret';
import { MemberTokenType } from '@app/shared/types/member_token_type';
import { SettingsService } from '@app/core/settings/settings.service';

const prepareUnsubscribePayload = ({ email, token, subunitId }: UnsubscribePayload): Record<string, string> => {
  let data: Record<string, string> = {
    email: email.trim(),
    token: token,
  };
  if (subunitId !== undefined && subunitId !== null) {
    data = {
      ...data,
      subunit_id: subunitId,
    };
  }
  return data;
};

@Injectable()
export class MembersApiV2Service {
  private http = inject(HttpClient);
  private baseParamsService = inject(BaseParamsService);
  private settingsService = inject(SettingsService);


  public isMember(
    memberIdentifier: MemberIdentifier,
    recaptchaData?: RecaptchaData,
  ): Observable<PublicInfoResponse> {
    const params = new HttpParams()
      .set('identifier_type', memberIdentifier.identifier_type)
      .set('identifier', encodeURIComponent(memberIdentifier.identifier));
    const options = recaptchaData ? { params, headers: createRecaptchaHeaders(recaptchaData) } : { params };

    return this.http.get<PublicInfoResponse>(
      `${this.getBaseUrl()}/public_info`,
      options,
    ).pipe(
      catchError(error => {
        if (isRecaptchaTooLowScoreHttpResponse(error)) {
          throw new TooLowRecaptchaScoreError();
        }
        throw error;
      }),
    );
  }

  public sendRegistrationPassword(memberIdentifier: string | undefined, recaptchData: RecaptchaData): Observable<unknown> {
    return this.http.post(
      `${this.getBaseUrl()}/send_registration_password`,
      { msisdn: memberIdentifier, language: this.settingsService.settings.locale },
      {
        headers: createRecaptchaHeaders(recaptchData),
      },
    ).pipe(
      catchError(error => {
        if (isRecaptchaTooLowScoreHttpResponse(error)) {
          throw new TooLowRecaptchaScoreError();
        }
        throw error;
      }),
    );
  }

  public changePassword(body: unknown): Observable<unknown> {
    return this.http.put(
      `${this.getBaseUrl()}/update_password`,
      body,
      { headers: this.baseParamsService.getAuthHeaders() },
    );
  }

  public resetPassword(email: string): Observable<ClientSecret> {
    return this.http.post<ClientSecret>(`${this.getBaseUrl()}/send_password_reset_token`, { email });
  }

  public verifyToken({
                       identifier,
                       type,
                       token,
                       identifierType,
                     }: {
                       identifier?: string;
                       type: MemberTokenType;
                       token: string | null;
                       identifierType: IdentifierType;
                     },
  ): Observable<boolean> {
    return this.http.get<{ valid: boolean }>(
      `${this.getBaseUrl()}/verify_token`,
      {
        params: new HttpParams().set(identifierType, identifier?.trim() || '')
          .set('type', type)
          .set('token', token || ''),
      },
    ).pipe(
      map(json => json.valid),
    );
  }

  public setPassword({ email, token, password }: {
    email: string | null;
    token: string | null;
    password: string;
  }): Observable<unknown> {
    return this.http.put(`${this.getBaseUrl()}/reset_password`, {
      email: email?.trim(),
      token: token,
      password: password,
    });
  }

  public create(body: CreateMemberRequestData): Observable<CreateMemberResponse> {
    return this.http.post<{ auth_token: string }>(
      `${this.getBaseUrl()}/create_with_autologin`,
      body,
    );
  }

  public update(body: CreateMemberRequestData): Observable<any> {
    return this.http.put(
      this.getBaseUrl(),
      body,
      { headers: this.baseParamsService.getAuthHeaders() },
    );
  }

  public updateConsents(body: unknown): Observable<unknown> {
    return this.http.put(
      `${this.getBaseUrl()}/update_consents`,
      body,
      { headers: this.baseParamsService.getAuthHeaders() },
    );
  }

  public destroy(): Observable<unknown> {
    return this.http.delete(
      this.getBaseUrl(),
      {
        headers: this.baseParamsService.getAuthHeaders(),
      },
    );
  }

  public fetchMember(): Observable<Partial<MemberInterface> | null> {
    return this.http.get<Partial<MemberInterface>>(
      this.getBaseUrl(),
      {
        headers: this.baseParamsService.getAuthHeaders(),
        params: new HttpParams().set('source', this.baseParamsService.source),
      },
    );
  }

  public unsubscribeEmail(payload: UnsubscribePayload): Observable<unknown> {
    return this.http.put(
      `${this.getBaseUrl()}/unsubscribe`,
      prepareUnsubscribePayload(payload),
    );
  }

  public verifyMsisdn(msisdn: string | null, token: string | null): Observable<unknown> {
    return this.http.put(
      `${this.getBaseUrl()}/msisdns/verify`,
      { msisdn: msisdn, token: token },
    );
  }

  public verifyEmail(email: string | null, token: string | null): Observable<unknown> {
    return this.http.put(
      `${this.getBaseUrl()}/emails/verify`,
      { email: email?.trim(), token: token },
    );
  }

  public sendVerificationEmail(email: string, token?: string | null): Observable<unknown> {
    const body: Record<string, unknown> = { email: email.trim() };
    if (token) {
      body.token = token;
    }

    return this.http.put(
      `${this.getBaseUrl()}/emails/send_verification_email`,
      body,
      !token ? { headers: this.baseParamsService.getAuthHeaders() } : {},
    );
  }

  public sendVerificationSms(msisdn: string, token?: string | null): Observable<unknown> {
    const body: Record<string, unknown> = { msisdn: msisdn };
    if (token) {
      body.token = token;
    }

    return this.http.put(
      `${this.getBaseUrl()}/msisdns/send_verification_sms`,
      body,
      !token ? { headers: this.baseParamsService.getAuthHeaders() } : {},
    );
  }

  public verifyStatus(token: string): Observable<any> {
    return this.http.get(
      `${this.getBaseUrl()}/verification_status`,
      { params: new HttpParams().set('token', token) },
    );
  }

  public verifyMyStatus(): Observable<any> {
    return this.http.get(
      `${this.getBaseUrl()}/my_verification_status`,
      { headers: this.baseParamsService.getAuthHeaders() },
    );
  }

  private getBaseUrl(): string {
    return `${environment.webforms_api}/v2/${this.baseParamsService.slug}/members`;
  }
}
